import { useId, type ComponentType, type ReactNode } from 'react';
import { cn } from '../../utils/cn';

export interface DetailSection<T extends string = string> {
  id: T;
  label: string;
  icon: ComponentType<{ className?: string }>;
  group: string;
  description: string;
  count?: number;
}

/** One-click section navigation, grouped by the work people come here to do. */
export function DetailSections<T extends string>({ sections, active, onChange, children, navTour }: {
  sections: DetailSection<T>[];
  active: T;
  onChange: (id: T) => void;
  children: ReactNode;
  /** `data-tour` value for the section navigation, so a page tour can point at it. */
  navTour?: string;
}) {
  const id = useId();
  const groups = [...new Set(sections.map(section => section.group))];
  const current = sections.find(section => section.id === active);
  return <div className="glass-card min-w-0 grid xl:grid-cols-[13rem_minmax(0,1fr)]">
    <nav aria-label="Page sections" data-tour={navTour} className="min-w-0 border-b xl:border-b-0 xl:border-r border-glass-200/10 p-3 xl:self-start xl:sticky xl:top-3 xl:max-h-[calc(100dvh-3rem)] xl:overflow-y-auto">
      <label className="block xl:hidden">
        <span className="block text-xs font-medium text-glass-400 mb-1.5">Go to section</span>
        <select className="glass-select w-full min-h-[44px]" value={active} onChange={e => onChange(e.target.value as T)} aria-controls={`${id}-content`}>
          {groups.map(group => <optgroup key={group} label={group}>
            {sections.filter(section => section.group === group).map(section => <option key={section.id} value={section.id}>
              {section.label}{section.count !== undefined ? ` (${section.count})` : ''}
            </option>)}
          </optgroup>)}
        </select>
      </label>
      <div className="hidden xl:block space-y-4">
        {groups.map(group => <div key={group}>
          <p className="eyebrow px-2 mb-1">{group}</p>
          <div className="space-y-0.5">
            {sections.filter(section => section.group === group).map(section => {
              const Icon = section.icon;
              return <button key={section.id} type="button" onClick={() => onChange(section.id)}
                aria-current={active === section.id ? 'page' : undefined} aria-controls={`${id}-content`}
                className={cn('w-full min-h-[44px] flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-left transition-colors',
                  active === section.id ? 'bg-accent-500/15 text-accent-300 font-medium' : 'text-glass-300 hover:bg-glass-200/10 hover:text-white')}>
                <Icon className="w-4 h-4 shrink-0" /><span className="flex-1 min-w-0">{section.label}</span>
                {section.count !== undefined && <span className="text-xs tabular-nums text-glass-400">{section.count}</span>}
              </button>;
            })}
          </div>
        </div>)}
      </div>
    </nav>
    <section id={`${id}-content`} aria-label={current?.label} className="min-w-0 p-4 md:p-5">
      <p className="text-sm text-glass-400 mb-5 pb-4 border-b border-glass-200/10">{current?.description}</p>
      {children}
    </section>
  </div>;
}
